import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Input, Icon, Segment } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { callApi } from '../../utils/api';

const ChatWidget = ({ props, user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isVisible, setIsVisible] = useState(() => {
        return localStorage.getItem('chatWidgetVisible') !== 'false';
    });
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([
        { id: 0, text: 'Soy un asistente virtual automatizado, no una persona real. No tengo la capacidad de resolver problemas, únicamente registro reportes para ser resueltos por el personal.', sender: 'bot', type: 'system' },
        { id: 1, text: 'Hola, este chat es para reportar hallazgos en el sistema. Por favor, selecciona el módulo donde ocurrió el hallazgos.', sender: 'bot' }
    ]);
    const [step, setStep] = useState('WAITING_FOR_MODULE');
    const [reportData, setReportData] = useState({ module: '', process: '', description: '', image: null, perfil: '' });
    const fileInputRef = React.useRef(null);
    const messagesEndRef = React.useRef(null);

    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, isOpen]);

    const toggleChat = () => setIsOpen(!isOpen);

    const addMessage = (text, sender, stepContext = null) => {
        setMessages(prev => [...prev, { id: Math.random(), text, sender, stepContext }]);
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };
    const handleProcessData = async (data) => {
        addMessage('Enviando reporte...', 'bot');

        data.sys_company = user || 'Error al obtener el usuario';
        data.perfil = JSON.stringify(
            Object.fromEntries(//Filtrar para mandar unicamente el que sea true
                Object.entries({
                    isAuthenticated: props?.isAuthenticated,
                    isClientAuthenticated: props?.isClientAuthenticated,
                    isDriverAuthenticated: props?.isDriverAuthenticated,
                    isSupplierAuthenticated: props?.isSupplierAuthenticated,
                }).filter(([_, v]) => v === true)
            )
        );


        if (data.image && data.image instanceof File) {
            try {
                data.image = await fileToBase64(data.image);
            } catch (err) {
                console.error("Error converting image:", err);
                addMessage('Error al procesar la imagen.', 'bot');
                return;
            }
        }
        try {
            const response = await callApi(`/incidents_CRMTickets/send-ticket`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            if (response.body.success) {
                addMessage(response.body.message, 'bot');
                setReportData({ module: '', process: '', description: '', image: null, perfil: '' });
                setStep('COMPLETED');
            } else {
                addMessage(response.body.message || 'Error al enviar el reporte.', 'bot');
            }
            console.log("Respuesta de la API:", response);
        } catch (error) {
            addMessage('Error al enviar el reporte.', 'bot');
            console.error('Error al enviar el reporte:', error);
        }
    };

    const modules = [
        "", "Catálogos", "Configuración", "Suministro", "Compras", "Ventas",
        "Transporte de Mercancía", "Facturación USA", "Gastos", "Transporte de Personal",
        "Tráfico", "Llantas", "Documentos", "Almacén", "Dashboard", "Formularios",
        "Bancos y Contabilidad", "Taller", "Activos", "Caseta", "Prospectos",
        "Inspección", "Incidencias", "Gestión de Tareas Internas", "Sellos", "Reportes", "Nómina"
    ];

    const handleUndo = (msgIndex, msgStepContext) => {
        if (!msgStepContext) return;

        // Regresar al paso donde se envió el mensaje
        setStep(msgStepContext);

        // Eliminar este mensaje y todos los siguientes
        setMessages(prev => prev.slice(0, msgIndex));

        // Limpiar datos según el paso al que regresamos
        setReportData(prev => {
            const newData = { ...prev };
            // Si regresamos a WAITING_FOR_MODULE, limpiamos todo
            if (msgStepContext === 'WAITING_FOR_MODULE') {
                return { module: '', process: '', description: '', image: null, perfil: '' };
            }
            // Si regresamos a WAITING_FOR_PROCESS, conservamos module, limpiamos el resto
            if (msgStepContext === 'WAITING_FOR_PROCESS') {
                return { ...newData, process: '', description: '', image: null };
            }
            // Si regresamos a WAITING_FOR_PROBLEM, conservamos module y process
            if (msgStepContext === 'WAITING_FOR_PROBLEM') {
                return { ...newData, description: '', image: null };
            }
            // Si regresamos a WAITING_FOR_IMAGE_CONFIRM, conservamos hasta description
            if (msgStepContext === 'WAITING_FOR_IMAGE_CONFIRM') {
                return { ...newData, image: null };
            }
            // WAITING_FOR_IMAGE: Usuario dijo SI. Regresamos a WAITING_FOR_IMAGE_CONFIRM (paso anterior lógico)
            // Pero el mensaje "Sí" se envió en WAITING_FOR_IMAGE_CONFIRM. Así que msgStepContext ya es correcto.

            return newData;
        });
    };

    const handleBotResponse = (userText, userImage = null) => {
        let nextStep = step;
        let nextData = { ...reportData };

        if (step === 'COMPLETED') {
            addMessage('Para iniciar un nuevo reporte, por favor selecciona el módulo donde ocurrió el hallazgo.', 'bot');
            setReportData({ module: '', process: '', description: '', image: null, perfil: '' });
            setStep('WAITING_FOR_MODULE');
            return;
        }

        if (step === 'WAITING_FOR_MODULE') {
            const module = userText.trim();
            if (!module || module === "" || !modules.includes(module)) {
                addMessage('Por favor selecciona un módulo válido de la lista.', 'bot');
                return;
            }
            nextData.module = module;
            addMessage('Ahora, especifica el proceso (Proveedores, Clientes, Requisición, Unidades, Etc).', 'bot');
            nextStep = 'WAITING_FOR_PROCESS';
        } else if (step === 'WAITING_FOR_PROCESS') {
            const process = userText.trim();
            if (!process || process.length < 3) {
                addMessage('Por favor especifica un proceso válido (mínimo 3 caracteres).', 'bot');
                return;
            }
            nextData.process = process;
            addMessage('Ahora, describe detalladamente el hallazgo que encontraste.', 'bot');
            nextStep = 'WAITING_FOR_PROBLEM';
        } else if (step === 'WAITING_FOR_PROBLEM') {
            nextData.description = userText;
            addMessage('¿Deseas adjuntar una imagen? (Si/No)', 'bot');
            nextStep = 'WAITING_FOR_IMAGE_CONFIRM';
        } else if (step === 'WAITING_FOR_IMAGE_CONFIRM') {
            const lowerText = userText.toLowerCase();
            if (['si', 'sí', 'yes', 's'].some(w => lowerText.includes(w))) {
                addMessage('Por favor, adjunta la imagen usando el botón de clip.', 'bot');
                nextStep = 'WAITING_FOR_IMAGE';
            } else {
                handleProcessData(nextData);
                nextStep = 'COMPLETED';
            }
        } else if (step === 'WAITING_FOR_IMAGE') {
            if (userImage) {
                nextData.image = userImage;
                addMessage(`Imagen "${userImage.name}" recibida.`, 'bot');
                handleProcessData(nextData);
                nextStep = 'COMPLETED';
            } else {
                const lowerText = userText.toLowerCase();
                if (['no', 'cancelar', 'omitir', 'na'].some(w => lowerText === w || lowerText.includes(w))) {
                    addMessage('Entendido. Enviando reporte sin imagen...', 'bot');
                    handleProcessData(nextData);
                    nextStep = 'COMPLETED';
                } else {
                    addMessage('Por favor sube una imagen o escribe "no" para omitir.', 'bot');
                }
            }
        }

        setReportData(nextData);
        setStep(nextStep);
    };

    const handleSendMessage = () => {
        if (message.trim()) {
            addMessage(message, 'user', step); // Guardar el paso actual como contexto
            handleBotResponse(message);
            setMessage('');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Mostrar mensaje de usuario con nombre de archivo o imagen
            addMessage(`[Adjunto: ${file.name}]`, 'user', step);
            handleBotResponse(null, file);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handlePaste = (e) => {
        if (step !== 'WAITING_FOR_IMAGE') return;

        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    // Mostrar mensaje de adjunto como ya haces
                    addMessage(`[Adjunto: imagen pegada]`, 'user', step);
                    handleBotResponse(null, file);
                    e.preventDefault(); // evita pegar texto/base64 en el input
                    return;
                }
            }
        }
    };


    if (!isVisible) return null;

    return ReactDOM.createPortal(
        <div
            className="chat-widget"
            style={{
                position: 'fixed',
                bottom: '20px',
                right: isOpen || isHovered ? '0px' : '-275px', // Slide the whole container
                zIndex: 100000,
                fontFamily: 'Arial, sans-serif',
                transition: 'right 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)', // Smooth ease-out
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end', // Align items to the right
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className="chat-widget__header"
                onClick={toggleChat}
                style={{
                    backgroundColor: '#2185d0',
                    color: 'white',
                    padding: '8px 8px',
                    borderRadius: isOpen
                        ? '12px 12px 0 0'
                        : '30px 0 0 30px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '320px', // Fixed width for container
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    justifyContent: 'flex-start'
                }}
            >
                <Icon name="chat" size="large" style={{ marginLeft: '5px' }} />

                <span style={{
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    flex: 1
                }}>
                    Reporte de hallazgos
                </span>

                {!isOpen && (
                    <Icon
                        name="close"
                        style={{ cursor: 'pointer', marginRight: '10px', marginBottom: '5px' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsVisible(false);
                            localStorage.setItem('chatWidgetVisible', 'false');
                        }}
                        title="Ocultar chat"
                    />
                )}

                {isOpen && <Icon name="chevron down" />}
            </div>


            {isOpen && (
                <Segment className="chat-widget__panel" attached='bottom' style={{
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    height: '400px',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div className="chat-widget__transcript" style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '15px',
                        backgroundColor: '#f4f7f6',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '320px'
                    }}>
                        {messages.map((msg) => (
                            <div key={msg.id} style={{
                                alignSelf: msg.type === 'system' ? 'center' : (msg.sender === 'user' ? 'flex-end' : 'flex-start'), // Centrar si es sistema
                                marginBottom: '12px',
                                maxWidth: msg.type === 'system' ? '95%' : '85%',
                                width: msg.type === 'system' ? '100%' : 'auto', // Ocupar ancho para centrado
                                textAlign: msg.type === 'system' ? 'center' : 'left'
                            }}>
                                <div
                                    className={`chat-widget__message-bubble ${msg.type === 'system'
                                        ? 'chat-widget__message-bubble--system'
                                        : msg.sender === 'user'
                                            ? 'chat-widget__message-bubble--user'
                                            : 'chat-widget__message-bubble--bot'}`}
                                    style={{
                                    padding: msg.type === 'system' ? '0' : '10px 14px', // Sin padding si es leyenda
                                    borderRadius: msg.type === 'system' ? '0' : '18px',
                                    borderBottomRightRadius: msg.sender === 'user' ? '4px' : '18px',
                                    borderBottomLeftRadius: msg.sender !== 'user' ? '4px' : '18px',
                                    backgroundColor: msg.type === 'system' ? 'transparent' : (msg.sender === 'user' ? '#2185d0' : '#ffffff'), // Transparente si es sistema
                                    color: msg.type === 'system' ? '#888' : (msg.sender === 'user' ? 'white' : '#333'), // Color opaco/gris
                                    boxShadow: msg.type === 'system' ? 'none' : '0 1px 2px rgba(0,0,0,0.1)', // Sin sombra
                                    fontSize: msg.type === 'system' ? '11px' : '14px', // Letra más pequeña
                                    lineHeight: '1.4',
                                    fontStyle: msg.type === 'system' ? 'italic' : 'normal'
                                }}>
                                    {msg.text}
                                    {msg.sender === 'user' && step !== 'COMPLETED' && (
                                        <Icon
                                            name="undo"
                                            style={{
                                                marginLeft: '8px',
                                                cursor: 'pointer',
                                                opacity: 0.7
                                            }}
                                            onClick={() => handleUndo(messages.indexOf(msg), msg.stepContext)}
                                            title="Deshacer este paso"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className="chat-widget__composer" style={{
                        padding: '12px',
                        borderTop: '1px solid #eaeaea',
                        backgroundColor: 'white',
                        borderRadius: '0 0 12px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            accept="image/*"
                        />
                        <Icon
                            name='paperclip'
                            size='large'
                            className={step === 'WAITING_FOR_IMAGE'
                                ? 'chat-widget__attachment'
                                : 'chat-widget__attachment chat-widget__attachment--disabled'}
                            style={{
                                cursor: step === 'WAITING_FOR_IMAGE' ? 'pointer' : 'default',
                                color: step === 'WAITING_FOR_IMAGE' ? '#2185d0' : '#ccc',
                                marginRight: '10px'
                            }}
                            onClick={() => step === 'WAITING_FOR_IMAGE' && fileInputRef.current.click()}
                        />
                        {step === 'WAITING_FOR_MODULE' ? (
                            <select
                                className="chat-widget__module-select"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={handleKeyPress}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="">Seleccione</option>
                                {modules.filter(m => m !== "").map((mod) => (
                                    <option key={mod} value={mod}>{mod}</option>
                                ))}
                            </select>
                        ) : (
                            <Input
                                className="chat-widget__message-input"
                                fluid
                                transparent
                                placeholder={step === 'WAITING_FOR_IMAGE' ? 'Sube una imagen...' : 'Escribe aquí...'}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                style={{ flex: 1 }}
                                onPaste={handlePaste}
                            />
                        )}
                        <Icon
                            name='send'
                            color='blue'
                            style={{ cursor: 'pointer', marginLeft: '10px' }}
                            onClick={handleSendMessage}
                        />
                    </div>
                </Segment>
            )}
        </div>,
        document.body
    );
};

export default ChatWidget;
